import React, { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import {
  IPauseScheduleMutation,
  IPauseScheduleMutationVariables,
  IResumeScheduleMutation,
  IResumeScheduleMutationVariables,
  IScheduleFieldsFragment,
  PermissionKey,
} from '../../../utils/apollo/graphqlSchema';
import {
  PAUSE_SCHEDULE,
  RESUME_SCHEDULE,
} from '../../../utils/apollo/scheduler/mutation';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { updateDictionaryItemAction } from '../../../redux/slices/dictionary';
import { DictionaryTypeEnum } from '../../../utils/interfaces';
import { useDispatch } from 'react-redux';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../PermissionControls/PermissionElementWrapper';
import toastMessage from '../../../utils/toastMessage';
import { SingleSelectOption } from '../../../components/Dropdown/SingleSelect';

interface IProps {
  schedule: IScheduleFieldsFragment;
  dictionaryType:
    | DictionaryTypeEnum.ScheduleAnalysis
    | DictionaryTypeEnum.ScheduleDashboard
    | DictionaryTypeEnum.ScheduleCohort;
  requiredPermissionKeys: PermissionKey[];
}

const SchedulePauseResumeMenuOption = ({
  schedule,
  dictionaryType,
  requiredPermissionKeys,
}: IProps) => {
  const dispatch = useDispatch();
  const { checkIsPermitted } = usePermission();
  const isPermittedToEditSchedule = checkIsPermitted({
    requiredPermissionKeys,
  });
  const { id, paused } = schedule;
  const [pauseSchedule] = useMutation<
    IPauseScheduleMutation,
    IPauseScheduleMutationVariables
  >(PAUSE_SCHEDULE);

  const handlePause = useCallback(async () => {
    const res = await pauseSchedule({ variables: { id } });
    if (printGraphqlErrors(res?.data?.pauseSchedule?.errors)) {
      return;
    }
    toastMessage({
      message: `${schedule?.name} paused.`,
      type: 'info',
    });
    dispatch(
      updateDictionaryItemAction({
        item: { ...schedule, paused: true },
        dictionaryType,
      }),
    );
  }, [dispatch, pauseSchedule, id, schedule, dictionaryType]);

  const [resumeSchedule] = useMutation<
    IResumeScheduleMutation,
    IResumeScheduleMutationVariables
  >(RESUME_SCHEDULE);

  const handleResume = useCallback(async () => {
    const res = await resumeSchedule({ variables: { id } });
    if (printGraphqlErrors(res?.data?.resumeSchedule?.errors)) {
      return;
    }
    toastMessage({
      message: `${schedule?.name} resumed.`,
      type: 'info',
    });
    dispatch(
      updateDictionaryItemAction({
        item: { ...schedule, paused: false },
        dictionaryType,
      }),
    );
  }, [dispatch, resumeSchedule, id, schedule, dictionaryType]);

  return (
    <PermissionElementWrapper isPermitted={isPermittedToEditSchedule}>
      {paused ? (
        <SingleSelectOption
          onClick={handleResume}
          iconName="play"
          label={'Resume'}
          disabled={!isPermittedToEditSchedule}
        />
      ) : (
        <SingleSelectOption
          onClick={handlePause}
          iconName="pause"
          label={'Pause'}
          disabled={!isPermittedToEditSchedule}
        />
      )}
    </PermissionElementWrapper>
  );
};

export default SchedulePauseResumeMenuOption;
